import { Expand01Icon, XCloseIcon } from '@langchain/untitled-ui-icons';
import { Modal, ModalClose, ModalDialog } from '@mui/joy';

import { useState } from 'react';

import '@/Pages/Graph/src/data/message';
import { renderAttachmentIcon } from '@/Pages/Run/utils/multimodalRunUtils';
import { MultimodalContent } from '@/components/RichTextEditor/MultimodalContent';
import { IconButton, Text, Tooltip } from '@/design-system';
import {
  MessageContentPartImageUrl,
  MessageContentPartLangChainMultimodal,
  MessageContentPartOpenAIFile,
} from '@/types/schema';

import { constructFileFromMessageContentPart } from './utils';

type FilesPreviewProps = (
  | { files: Set<File>; messageContentParts?: never }
  | {
      files?: never;
      messageContentParts: (
        | MessageContentPartImageUrl
        | MessageContentPartOpenAIFile
        | MessageContentPartLangChainMultimodal
      )[];
    }
) & {
  handleDeleteFile?: ({ index, file }: { index: number; file?: File }) => void;
};

type SelectedFileInfo = {
  name: string;
  type: string;
  data?: string;
};

export const FilesPreview = ({
  files,
  messageContentParts,
  handleDeleteFile,
}: FilesPreviewProps) => {
  const filesArray = files
    ? Array.from(files)
    : messageContentParts
        ?.filter(
          (c) =>
            c.type === 'file' || c.type === 'image_url' || c.type === 'image'
        )
        .map(constructFileFromMessageContentPart);

  const [expandedFile, setExpandedFile] = useState<SelectedFileInfo>();

  if (!filesArray.length) return null;

  return (
    <>
      <div className="flex max-h-[100px] w-full flex-wrap items-center gap-2 overflow-y-auto">
        {filesArray.map((f, index) => (
          <div
            key={f.name ?? index}
            className="flex items-center gap-1 rounded-md border border-secondary px-1.5 py-1"
          >
            <Tooltip title="View file">
              <button
                type="button"
                onClick={() => setExpandedFile(f)}
                className="group/file-button flex items-center gap-1"
              >
                {/* Attachment icon - shown by default, hidden on hover */}
                <div className="group-hover/file-button:hidden">
                  {renderAttachmentIcon(f.type, 'small')}
                </div>
                {/* Expand icon - hidden by default, shown on hover */}
                <div className="hidden group-hover/file-button:block">
                  <Expand01Icon className="h-4 w-4" />
                </div>
                <Text variant="sm"> {f.name}</Text>
              </button>
            </Tooltip>

            {handleDeleteFile && (
              <IconButton
                icon={XCloseIcon}
                label="Remove file"
                onClick={() => handleDeleteFile({ index, file: f })}
                color="secondary"
                variant="plain"
                className="bg-transparent"
              />
            )}
          </div>
        ))}
      </div>
      {expandedFile && (
        <Modal open={true} onClose={() => setExpandedFile(undefined)}>
          <ModalDialog
            style={{
              maxWidth: '500px',
              maxHeight: '500px',
              overflow: 'auto',
            }}
          >
            <div className="flex flex-col gap-2">
              <ModalClose />
              <MultimodalContent
                contentType={expandedFile.type}
                url={
                  'data' in expandedFile
                    ? expandedFile.data
                    : URL.createObjectURL(expandedFile as File)
                }
                contentName={expandedFile.name}
                unstyled={true}
              />
            </div>
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};
